/**
 * Routes, and the guards on them.
 *
 * `RequirePermission` keeps a route out of reach of someone who lacks the
 * permission. It is a convenience for the person using the app, not a security
 * boundary — the API refuses the underlying calls regardless, which is the check
 * that actually matters.
 */

import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useI18n } from "../shared/i18n";
import { AuditPage } from "../features/admin/AuditPage";
import { RolesPage } from "../features/admin/RolesPage";
import { UsersPage } from "../features/admin/UsersPage";
import { LoginPage } from "../features/auth/LoginPage";
import { useSession } from "../features/auth/session";
import { DashboardPage } from "../features/dashboard/DashboardPage";
import { AppShell } from "./AppShell";

function RequirePermission({ permission, children }: { permission: string; children: ReactNode }) {
  const { can } = useSession();
  const { t } = useI18n();
  if (!can(permission)) {
    return (
      <div className="empty">
        <h2>{t("denied.title")}</h2>
        <p>{t("denied.body", { permission })}</p>
      </div>
    );
  }
  return <>{children}</>;
}

export function AppRoutes() {
  const { session, loading } = useSession();

  if (loading) {
    // Deliberately plain: a spinner that flashes for 80ms is worse than nothing.
    return <div className="booting" aria-busy="true" />;
  }

  if (!session) return <LoginPage />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<DashboardPage />} />
        <Route
          path="/admin"
          element={
            <RequirePermission permission="admin:users">
              <UsersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <RequirePermission permission="admin:roles">
              <RolesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <RequirePermission permission="admin:audit">
              <AuditPage />
            </RequirePermission>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
